import { useState, useMemo } from 'react'
import type { Apu, AiuApu } from './tipos'
import { IconoCerrar } from '@/shared/ui/Iconos/Iconos'
import estilos from './BancoApus.module.css'

function formatearMoneda(val: number) {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    maximumFractionDigits: 0,
  }).format(val)
}

type Props = {
  abierto: boolean
  apu: Apu | null
  onCerrar: () => void
  onGuardar: (id: string, aiu: AiuApu) => void
}

export function ModalEditarAiuApu({ abierto, apu, onCerrar, onGuardar }: Props) {
  const [admin, setAdmin] = useState<number>(() => apu?.aiu.porcentaje_admin ?? 10)
  const [imprevistos, setImprevistos] = useState<number>(() => apu?.aiu.porcentaje_imprevistos ?? 0)
  const [utilidad, setUtilidad] = useState<number>(() => apu?.aiu.porcentaje_utilidad ?? 3)
  const [ivaUtilidad, setIvaUtilidad] = useState<number>(() => apu?.aiu.porcentaje_iva_utilidad ?? 19)

  // Reset cuando cambia el APU
  const apuId = apu?.id
  useMemo(() => {
    if (apu) {
      setAdmin(apu.aiu.porcentaje_admin)
      setImprevistos(apu.aiu.porcentaje_imprevistos)
      setUtilidad(apu.aiu.porcentaje_utilidad)
      setIvaUtilidad(apu.aiu.porcentaje_iva_utilidad)
    }
  }, [apuId, apu])

  if (!abierto || !apu) return null

  const costoDirecto = apu.costo_directo || 0
  const valorAdmin = Math.round(costoDirecto * (admin / 100))
  const valorImprevistos = Math.round(costoDirecto * (imprevistos / 100))
  const valorUtilidad = Math.round(costoDirecto * (utilidad / 100))
  const valorIvaUtilidad = Math.round(valorUtilidad * (ivaUtilidad / 100))
  const totalAiu = valorAdmin + valorImprevistos + valorUtilidad + valorIvaUtilidad
  const precioFinal = costoDirecto + totalAiu

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    onGuardar(apu!.id, {
      porcentaje_admin: Number(admin) || 0,
      porcentaje_imprevistos: Number(imprevistos) || 0,
      porcentaje_utilidad: Number(utilidad) || 0,
      porcentaje_iva_utilidad: Number(ivaUtilidad) || 0,
    })
    onCerrar()
  }

  return (
    <div className={estilos.modalVelo} onClick={onCerrar}>
      <div className={estilos.modalCajaAiu} onClick={(e) => e.stopPropagation()}>
        <header className={estilos.modalHeader}>
          <div>
            <h3>Ajuste de AIU y Margen Contractual</h3>
            <span style={{ fontSize: '0.78rem', opacity: 0.85 }}>{apu.codigo} · {apu.nombre}</span>
          </div>
          <button type="button" className={estilos.btnCerrarModal} onClick={onCerrar} title="Cerrar">
            <IconoCerrar />
          </button>
        </header>

        <form onSubmit={handleSubmit}>
          <div className={estilos.modalCuerpoAiu}>
            <div className={estilos.resumenDirectoAiu}>
              <div>
                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 800 }}>COSTO DIRECTO BASE</div>
                <div style={{ fontSize: '1.1rem', fontWeight: 900, color: '#0f172a' }}>{formatearMoneda(costoDirecto)}</div>
              </div>
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 800 }}>UNIDAD DE MEDIDA</div>
                <div style={{ fontSize: '0.95rem', fontWeight: 900, color: '#2d0177' }}>{apu.unidad}</div>
              </div>
            </div>

            <div className={estilos.grillaInputsAiu}>
              {/* Administración */}
              <div className={estilos.campoAiuItem}>
                <label htmlFor="input-admin">Administración (A)</label>
                <div className={estilos.inputAiuWrapper}>
                  <input
                    id="input-admin"
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={admin}
                    onChange={(e) => setAdmin(Number(e.target.value) || 0)}
                    className={estilos.inputAiuNum}
                  />
                  <span className={estilos.sufijoPct}>%</span>
                </div>
                <span className={estilos.calculoParcialAiu}>+ {formatearMoneda(valorAdmin)}</span>
              </div>

              {/* Imprevistos */}
              <div className={estilos.campoAiuItem}>
                <label htmlFor="input-imprevistos">Imprevistos (I)</label>
                <div className={estilos.inputAiuWrapper}>
                  <input
                    id="input-imprevistos"
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={imprevistos}
                    onChange={(e) => setImprevistos(Number(e.target.value) || 0)}
                    className={estilos.inputAiuNum}
                  />
                  <span className={estilos.sufijoPct}>%</span>
                </div>
                <span className={estilos.calculoParcialAiu}>+ {formatearMoneda(valorImprevistos)}</span>
              </div>

              {/* Utilidad */}
              <div className={estilos.campoAiuItem}>
                <label htmlFor="input-utilidad">Utilidad (U)</label>
                <div className={estilos.inputAiuWrapper}>
                  <input
                    id="input-utilidad"
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={utilidad}
                    onChange={(e) => setUtilidad(Number(e.target.value) || 0)}
                    className={estilos.inputAiuNum}
                  />
                  <span className={estilos.sufijoPct}>%</span>
                </div>
                <span className={estilos.calculoParcialAiu}>+ {formatearMoneda(valorUtilidad)}</span>
              </div>

              {/* IVA sobre Utilidad */}
              <div className={estilos.campoAiuItem}>
                <label htmlFor="input-iva-u">IVA s/ Utilidad (19%)</label>
                <div className={estilos.inputAiuWrapper}>
                  <input
                    id="input-iva-u"
                    type="number"
                    step="0.5"
                    min="0"
                    max="100"
                    value={ivaUtilidad}
                    onChange={(e) => setIvaUtilidad(Number(e.target.value) || 0)}
                    className={estilos.inputAiuNum}
                  />
                  <span className={estilos.sufijoPct}>%</span>
                </div>
                <span className={estilos.calculoParcialAiu}>+ {formatearMoneda(valorIvaUtilidad)}</span>
              </div>
            </div>

            {/* Tabla resumen dinámico */}
            <table className={estilos.tablaResumenAiu}>
              <tbody>
                <tr>
                  <td>Costo Directo (I + II + III + IV):</td>
                  <td>{formatearMoneda(costoDirecto)}</td>
                </tr>
                <tr>
                  <td>Total Costos Indirectos (AIU):</td>
                  <td style={{ color: '#be185d' }}>+ {formatearMoneda(totalAiu)}</td>
                </tr>
                <tr className={estilos.filaTotalDestacada}>
                  <td>PRECIO UNITARIO TOTAL:</td>
                  <td>{formatearMoneda(precioFinal)} / {apu.unidad}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <footer className={estilos.modalFooter}>
            <button type="button" className={estilos.btnCancelar} onClick={onCerrar}>
              Cancelar
            </button>
            <button type="submit" className={estilos.btnGuardar}>
              Guardar Nuevo AIU
            </button>
          </footer>
        </form>
      </div>
    </div>
  )
}

